import { makeGetCall } from '../effects/requests';
import { makePostCall } from '../effects/requests';
import { getLocalStorage } from '../utils';
import endpoints from '../utils/endpoints';
import { QuickReplyOption } from '../components/BookingAgent/quickReplyUtils';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  quick_replies?: Array<string | QuickReplyOption>;
  images?: string[];
}

export interface AgentProduct {
  product_id: string;
  name: string;
  brand: string;
  price: string;
  currency: string;
  image_url: string;
  variant_id: string | null;
  reason: string;
  previously_purchased: boolean;
}

export interface RequiresAction {
  type: 'otp_input' | 'deposit_payment' | 'booking_confirmed' | 'treatment_upsell' | 'product_recommendation';
  token?: string;
  mobile?: string;
  amount?: string;
  currency?: string;
  payment_link?: string | null;
  booking_id?: string;
  start_dt?: string;
  treatments?: Array<Record<string, unknown>>;
  products?: AgentProduct[];
  title?: string;
  description?: string;
}

export interface ChatResponse {
  reply: string;
  session_id: string;
  booking_state: Record<string, any>;
  requires_action: RequiresAction | null;
  quick_replies?: Array<string | QuickReplyOption>;
  user_image_urls?: string[];
}

export interface ChatSessionDisplayMessage {
  role: 'user' | 'assistant';
  content: string;
  images?: string[];
}

export interface ChatSessionResponse {
  id: string;
  stylist: string | null;
  client: string | null;
  booking_state: Record<string, any>;
  display_messages: ChatSessionDisplayMessage[];
  pending_requires_action?: RequiresAction | null;
}

export interface AgentVerifyOtpResponse {
  verified: boolean;
  client_id: string | null;
  is_new_client: boolean;
}

export interface AgentDepositIntentResponse {
  success: boolean;
  client_secret: string;
  payment_intent_id: string;
}

export interface ChatSessionOptions {
  stylistUniqueCode?: string;
  shopifyStylistPageId?: string;
  clientId?: string;
  apiBase?: string;
  webotpHost?: string;
}

export interface ChatMessageOptions {
  stylistUniqueCode?: string;
  clientId?: string;
  apiBase?: string;
  images?: string[];
  webotpHost?: string;
}

export const createChatSession = (
  opts: ChatSessionOptions = {},
): Promise<{ session_id: string; mode?: string; stylist_unique_code?: string; stylist_display_name?: string }> => {
  const payload: Record<string, unknown> = { client: opts.clientId ?? null };
  if (opts.stylistUniqueCode) {
    payload.stylist_unique_code = opts.stylistUniqueCode;
  } else if (opts.shopifyStylistPageId) {
    payload.shopify_stylist_page_id = opts.shopifyStylistPageId;
  }
  if (opts.webotpHost) {
    payload.webotp_host = opts.webotpHost;
  }
  return makePostCall({
    url: endpoints.agent_chat_session,
    apiPayload: payload,
    baseUrl: opts.apiBase || endpoints.API_BASE_URL,
  }).then((res: any) => res.data);
};

export const getChatSession = (
  sessionId: string,
  apiBase?: string,
): Promise<ChatSessionResponse> => {
  return makeGetCall({
    url: `${endpoints.agent_chat_session}${sessionId}/`,
    baseUrl: apiBase || endpoints.API_BASE_URL,
  }).then((res: any) => res.data);
};

export const sendChatMessage = (
  sessionId: string,
  message: string,
  opts: ChatMessageOptions = {},
): Promise<ChatResponse> => {
  return makePostCall({
    url: endpoints.agent_chat,
    apiPayload: {
      session_id: sessionId,
      message,
      images: opts.images ?? [],
      stylist_id: opts.stylistUniqueCode ?? null,
      client_id: opts.clientId ?? null,
      webotp_host: opts.webotpHost ?? '',
    },
    baseUrl: opts.apiBase || endpoints.API_BASE_URL,
  }).then((res: any) => res.data);
};

export const verifyAgentOtp = (
  sessionId: string,
  mobile: string,
  code: string,
  token: string,
): Promise<AgentVerifyOtpResponse> => {
  return makePostCall({
    url: endpoints.agent_verify_otp,
    apiPayload: {
      session_id: sessionId,
      mobile,
      code,
      token,
    },
  }).then((res: any) => res.data);
};

export const createAgentDepositIntent = (
  sessionId: string,
  amount: string,
): Promise<AgentDepositIntentResponse> => {
  return makePostCall({
    url: endpoints.agent_deposit_payment_intent,
    apiPayload: {
      session_id: sessionId,
      amount,
    },
  }).then((res: any) => res.data);
};

export const confirmAgentDeposit = (
  sessionId: string,
  paymentIntentId: string,
): Promise<{ success: boolean; deposit_paid: boolean }> => {
  return makePostCall({
    url: endpoints.agent_confirm_deposit,
    apiPayload: {
      session_id: sessionId,
      payment_intent_id: paymentIntentId,
    },
  }).then((res: any) => res.data);
};

export interface RetellWebCallResponse {
  access_token: string;
  call_id?: string;
  call_type?: string;
}

export const createRetellWebCall = (
  sessionId: string,
  apiBase?: string,
  webotpHost?: string,
): Promise<RetellWebCallResponse> => {
  return makePostCall({
    url: endpoints.agent_retell_web_call,
    apiPayload: {
      session_id: sessionId,
      webotp_host: webotpHost ?? '',
    },
    baseUrl: apiBase || endpoints.API_BASE_URL,
  }).then((res: any) => res.data);
};

/**
 * Synthesise an agent reply to speech via the backend (Amazon Polly).
 * The endpoint returns raw MP3 (audio/mpeg) on success, or the JSON status
 * envelope on error — so we bypass the axios layer (which unwraps that
 * envelope) and read the audio blob directly. Returns an object URL to play,
 * or null if synthesis failed (caller skips playback). The caller owns the
 * returned URL and must URL.revokeObjectURL it once playback finishes.
 */
export const speakReply = async (text: string, apiBase?: string): Promise<string | null> => {
  if (!text.trim()) return null;
  const base = (apiBase || endpoints.API_BASE_URL || '').replace(/\/$/, '');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const accessToken = getLocalStorage('access_token');
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  try {
    const res = await fetch(`${base}/${endpoints.agent_tts}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ text }),
    });
    const contentType = res.headers.get('content-type') ?? '';
    if (!res.ok || !contentType.includes('audio')) return null;
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
};
